import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import Index from "@/pages/Index";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Courses from "@/pages/Courses";
import CourseDetails from "@/pages/courses/CourseDetails";
import CourseLesson from "@/pages/courses/CourseLesson";
import EditCourse from "@/pages/courses/EditCourse";
import CreateCourse from "@/pages/courses/CreateCourse";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/blog/BlogPost";
import CreateBlog from "@/pages/blog/CreateBlog";
import EditBlog from "@/pages/blog/EditBlog";
import Projects from "@/pages/projects/ProjectsPage";
import CreateProject from "@/pages/projects/CreateProject";
import EditProject from "@/pages/projects/EditProject";
import AdminDashboard from "@/pages/AdminDashboard";
import FreelanceToolkit from "@/pages/FreelanceToolkit";
import ProposalGeneratorPage from "@/pages/toolkit/ProposalGeneratorPage";
import ProfileGeneratorPage from "@/pages/toolkit/ProfileGeneratorPage";
import ProfileReviewerPage from "@/pages/toolkit/ProfileReviewerPage";
import Profile from "@/pages/Profile";
import { AuthProvider } from "@/contexts/AuthContext";
import { DataProvider } from "@/contexts/DataContext";
import Unauthorized from "@/pages/Unauthorized";
import ProtectedRoute from "@/components/ProtectedRoute";
import NotFound from "@/pages/NotFound";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";

const App: React.FC = () => {
  return (
    <AuthProvider>
      <DataProvider>
        <div className="flex flex-col min-h-screen bg-background text-foreground">
          <Navbar />
          <main className="flex-grow">
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/courses" element={<Courses />} />
              <Route path="/courses/:id" element={<CourseDetails />} />
              <Route path="/courses/:courseId/lessons/:lessonId" element={<CourseLesson />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:id" element={<BlogPost />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/freelance-toolkit" element={<FreelanceToolkit />} />
              <Route path="/freelance-toolkit/proposal-generator" element={<ProposalGeneratorPage />} />
              <Route path="/freelance-toolkit/profile-generator" element={<ProfileGeneratorPage />} />
              <Route path="/freelance-toolkit/profile-reviewer" element={<ProfileReviewerPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              
              {/* Protected Routes */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courses/create"
                element={
                  <ProtectedRoute requiredRoles={["admin"]}>
                    <CreateCourse />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courses/:id/edit"
                element={
                  <ProtectedRoute requiredRoles={["admin"]}>
                    <EditCourse />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/blog/create"
                element={
                  <ProtectedRoute requiredRoles={["admin"]}>
                    <CreateBlog />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/blog/:id/edit"
                element={
                  <ProtectedRoute requiredRoles={["admin"]}>
                    <EditBlog />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/create"
                element={
                  <ProtectedRoute requiredRoles={["admin"]}>
                    <CreateProject />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:id/edit"
                element={
                  <ProtectedRoute requiredRoles={["admin"]}>
                    <EditProject />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredRoles={["admin"]}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Footer />
        </div>
        <Toaster />
      </DataProvider>
    </AuthProvider>
  );
};

export default App;